import { useEffect, useRef, useState } from "react";
import { isFileDrag } from "../../hooks/useFileDrop";
import {
  clearDragGhostTarget,
  isDragGhostSameFolder,
  isDragGhostSelf,
  setDragGhostTarget,
} from "../DragGhost/dragGhost";

// Типы данных, под которыми карточки кладут себя в dataTransfer
export const VERSION_DRAG_TYPE = "application/x-7cut-version";
export const FOLDER_DRAG_TYPE = "application/x-7cut-folder";

const hasType = (e, type) =>
  Array.from(e.dataTransfer?.types || []).includes(type);
const isVersionDrag = (e) => hasType(e, VERSION_DRAG_TYPE);
const isFolderDrag = (e) => hasType(e, FOLDER_DRAG_TYPE);

/**
 * Место, куда можно бросить карточку видео или папки (перенос)
 * или видеофайлы с диска (загрузка).
 *
 * onMoveVersion / onMoveFolder / onDropFiles — что принимает цель;
 *   не переданный обработчик — этот тип не принимается.
 * label, targetProjectId — название и id папки для подсказки у курсора.
 * isRoot — цель — корень пространства (targetProjectId не нужен).
 * Элемент из этого же места и папка сама в себя не принимаются: цель не
 * подсвечивается, у курсора — подсказка, почему нельзя.
 * children({ isOver, dropHandlers }) — render-prop.
 */
const DropTarget = ({
  disabled,
  onMoveVersion,
  onMoveFolder,
  onDropFiles,
  label,
  targetProjectId,
  isRoot = false,
  children,
}) => {
  const [isOver, setIsOver] = useState(false);
  const counter = useRef(0);

  // Конец любого перетаскивания (бросок в другом месте, Esc, отказ цели) —
  // сбросить счётчик наведения, иначе подсветка может остаться до следующего раза
  useEffect(() => {
    const reset = () => {
      if (counter.current === 0) return;
      counter.current = 0;
      setIsOver(false);
    };
    document.addEventListener("dragend", reset, true);
    document.addEventListener("drop", reset, true);
    return () => {
      document.removeEventListener("dragend", reset, true);
      document.removeEventListener("drop", reset, true);
    };
  }, []);

  // Перетаскивание карточки со страницы или из дерева, которое эта цель знает
  const isInternal = (e) =>
    !disabled &&
    ((isVersionDrag(e) && !!onMoveVersion) ||
      (isFolderDrag(e) && !!onMoveFolder));

  const accepts = (e) => {
    if (disabled) return false;
    if (isFileDrag(e)) return !!onDropFiles;
    if (!isInternal(e)) return false;
    if (isDragGhostSameFolder(targetProjectId, isRoot)) return false;
    if (isFolderDrag(e) && !isRoot && isDragGhostSelf(targetProjectId)) {
      return false;
    }
    return true;
  };

  const dropHandlers = {
    onDragEnter: (e) => {
      const internal = isInternal(e);
      const ok = accepts(e);
      if (!internal && !ok) return;
      // Цель, которая не принимает (своя папка, сама папка), всё равно
      // забирает событие и показывает у курсора, почему нельзя
      e.stopPropagation();
      if (ok) e.preventDefault();
      counter.current += 1;
      if (ok) setIsOver(true);
      if (internal) {
        setDragGhostTarget(counter, {
          label,
          projectId: targetProjectId,
          isRoot,
        });
      }
    },
    onDragLeave: (e) => {
      if (!isInternal(e) && !accepts(e)) return;
      e.stopPropagation();
      counter.current = Math.max(0, counter.current - 1);
      if (counter.current === 0) {
        setIsOver(false);
        clearDragGhostTarget(counter);
      }
    },
    onDragOver: (e) => {
      if (!accepts(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = isFileDrag(e) ? "copy" : "move";
    },
    onDrop: (e) => {
      if (!accepts(e)) return;
      e.preventDefault();
      e.stopPropagation();
      counter.current = 0;
      setIsOver(false);

      const readPayload = (type) => {
        try {
          return JSON.parse(e.dataTransfer.getData(type));
        } catch (err) {
          return null; // чужие данные с тем же типом — игнорируем
        }
      };

      if (isFolderDrag(e)) {
        const payload = readPayload(FOLDER_DRAG_TYPE);
        if (payload) onMoveFolder(payload);
        return;
      }
      if (isVersionDrag(e)) {
        const payload = readPayload(VERSION_DRAG_TYPE);
        if (payload) onMoveVersion(payload);
        return;
      }
      const files = Array.from(e.dataTransfer.files || []).filter((f) =>
        f.type.startsWith("video/")
      );
      if (files.length) onDropFiles(files);
    },
  };

  return children({ isOver, dropHandlers });
};

export default DropTarget;
